import { CheckCircleOMIcon, ErrorOMIcon, InfoOMIcon, LoadingSpinnerOMIcon, WarningOMIcon } from '@omnea-app/icons'
import { Thumbnail } from '../Thumbnail'
import type { Theme } from '@mui/material'
import type { VariantType } from 'notistack'
import type { ReactNode } from 'react'

declare module 'notistack' {
  interface VariantOverrides {
    // hyphen rather than . as this becomes a CSS class
    loading: true
  }
}

// The direct child of notistack's icon slot — a Thumbnail's root <div> for the badge variants, or
// the loading spinner's own root for `loading`, which stays bare (a spinner in a filled badge reads
// oddly, so it isn't wrapped in a Thumbnail).
const iconSlotSelector = '#notistack-snackbar > *'

// Global styles for notistack snackbars. notistack is not an MUI component, so
// it can't be themed via theme.components — it ships stable class names that we
// target through MUI's <GlobalStyles>.
export const snackbarStyles = (theme: Theme) => ({
  '.notistack-Snackbar': {
    '.notistack-MuiContent': {
      boxShadow: theme.customShadows.xs,
      borderRadius: theme.shape['radius-lg'],
      borderStyle: 'solid',
      borderWidth: theme.strokeWidth.default,
      borderColor: theme.palette.border.subtle,
      backgroundColor: theme.palette.bg.surface.elevated,
      color: theme.palette.text.default,
      padding: theme.spacing(2, 4),
      ...theme.typography['body-medium'],
      [`& ${iconSlotSelector}`]: {
        // Override notistack's inline style
        marginRight: `${theme.spacing(2.5)} !important`,
        alignSelf: 'flex-start',
      },
    },
    '.notistack-MuiContent-loading': {
      // Loading spinner is a span with an svg inside
      '& .MuiCircularProgress-root': {
        color: theme.palette.fg.default,
        '& svg': {
          // Override inline style and !important above
          marginRight: '0 !important',
        },
      },
    },
  },
})

export const snackbarIconVariants: Partial<Record<VariantType, ReactNode>> = {
  loading: (
    <Thumbnail size="small" color="subtle">
      <LoadingSpinnerOMIcon />
    </Thumbnail>
  ),
  success: (
    <Thumbnail size="small" color="success">
      <CheckCircleOMIcon />
    </Thumbnail>
  ),
  info: (
    <Thumbnail size="small" color="info">
      <InfoOMIcon />
    </Thumbnail>
  ),
  warning: (
    <Thumbnail size="small" color="warning">
      <WarningOMIcon />
    </Thumbnail>
  ),
  error: (
    <Thumbnail size="small" color="error">
      <ErrorOMIcon />
    </Thumbnail>
  ),
}
